import { FC, MouseEvent, useMemo, useState } from "react";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import {
	Box,
	Chip,
	IconButton,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { AdminProtected } from "@/components/Application/AdminProtected";
import { SATOSLink } from "@/components/Application/SATOSLink";
import { EditProcedureModal } from "@/components/Procedures/EditProcedure";
import { NodeDiagramButton } from "@/components/Procedures/NodeDiagram/utils/NodeDiagramButton";

import { Procedure } from "@/model/Procedure/Procedure";

dayjs.extend(relativeTime);

interface ProcedureListItemDesignProps {
	procedure: Procedure;
	/** Optional: render function for highlighting the name */
	renderLabel?: (name: string) => React.ReactNode;
}

type StatusToken = {
	label: string;
	color: "default" | "primary" | "success" | "warning" | "error" | "info";
};

const getStatusToken = (p: Procedure): StatusToken => {
	const code = (p as any)?.ProcedureStatusNum ?? (p as any)?.Status ?? -1;
	switch (code) {
		case 0:
			return { label: "Draft", color: "warning" };
		case 1:
			return { label: "In review", color: "info" };
		case 2:
			return { label: "Published", color: "success" };
		case 3:
			return { label: "Archived", color: "default" };
		default:
			return { label: "Unknown", color: "default" };
	}
};

export const ProcedureListItemDesign: FC<ProcedureListItemDesignProps> = ({
	procedure,
	renderLabel,
}) => {
	const theme = useTheme();
	const [editProcedureOpen, setEditProcedureOpen] = useState(false);

	const updatedISO = procedure?.Updated || procedure?.LastModified || "";
	const updatedLabel =
		updatedISO && updatedISO !== "0001-01-01T00:00:00"
			? dayjs(updatedISO).fromNow()
			: "—";
	const updatedTooltip =
		updatedISO && updatedISO !== "0001-01-01T00:00:00"
			? `Last updated: ${dayjs(updatedISO).format("DD/MM/YY, HH:mm:ss")}`
			: "Last updated: No update info";

	const UPDATED_CHIP_WIDTH = 100;

	const labelRowSx = {
		display: "flex",
		alignItems: "center",
		gap: 1,
		width: "100%",
		px: 1,
		py: 0.5,
		borderRadius: 1.25,
		transition: "background-color .15s ease",
		"&:hover": {
			backgroundColor:
				theme.palette.mode === "light"
					? alpha(theme.palette.text.primary, 0.04)
					: alpha(theme.palette.text.primary, 0.08),
		},
	} as const;

	const nameSx = {
		fontWeight: 700,
		flexGrow: 1,
		whiteSpace: "nowrap" as const,
		overflow: "hidden",
		textOverflow: "ellipsis",
		fontSize: { xs: 12.5, sm: 13.5, md: 14.5 },
		lineHeight: 1.25,
	};

	const metaChipSx = {
		height: 22,
		borderRadius: 1.25,
		width: UPDATED_CHIP_WIDTH,
		justifyContent: "center",
		"& .MuiChip-label": {
			px: 1,
			fontSize: 12,
			fontWeight: 700,
			width: "100%",
			textAlign: "center",
		},
	} as const;

	const updatedChip = (
		<Tooltip title={updatedTooltip} arrow>
			<Chip
				size="small"
				variant="outlined"
				label={updatedLabel}
				sx={metaChipSx}
			/>
		</Tooltip>
	);

	const name = procedure.Name || "Untitled procedure";
	const NameContent = renderLabel ? renderLabel(name) : name;

	return (
		<>
			<TreeItem
				itemId={`${procedure.Id}-procedure`}
				label={
					<Box sx={labelRowSx}>
						{/* Left: icon + name link */}
						<SATOSLink
							to="AccessProcedure"
							params={[procedure.Id]}
							className="accordion-item-link"
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
								textDecoration: "none",
								color: "inherit",
								minWidth: 0,
							}}
							stopPropagation={true}
						>
							<PlaylistAddCheckOutlinedIcon
								sx={{ fontSize: 20, color: theme.palette.primary.main }}
							/>
							<Box
								sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}
							>
								<Typography component="span" sx={nameSx} title={name}>
									{NameContent}
								</Typography>
							</Box>
						</SATOSLink>

						{/* Spacer */}
						<Box sx={{ flexGrow: 1 }} />

						{/* Middle-right: Node diagram + date chip */}
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								flexShrink: 0,
							}}
						>
							<NodeDiagramButton item={procedure} />
							{updatedChip}
						</Box>

						{/* Far right: lock + edit button */}
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.25,
								flexShrink: 0,
								ml: 0.5,
							}}
						>
							{procedure.WriteProtected && (
								<Tooltip title="Write protected" arrow>
									<LockOutlinedIcon
										fontSize="small"
										sx={{ color: alpha(theme.palette.text.primary, 0.6) }}
									/>
								</Tooltip>
							)}
							<AdminProtected>
								<Tooltip title="Edit procedure" arrow>
									<IconButton
										size="small"
										onClick={(e: MouseEvent<HTMLButtonElement>) => {
											e.stopPropagation();
											setEditProcedureOpen(true);
										}}
									>
										<EditOutlinedIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</AdminProtected>
						</Box>
					</Box>
				}
			/>

			<EditProcedureModal
				open={editProcedureOpen}
				onClose={() => setEditProcedureOpen(false)}
				id={procedure.Id}
			/>
		</>
	);
};
